import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { sumBy } from 'lodash/fp'
import * as v from 'valibot'

import type {
  UserAssignedGroupsDeleteRequest,
  UserAssignedGroupsDeleteResult,
  UserAssignedGroupsPostRequest,
  UserAssignedGroupsResult,
  UserSearch,
} from '@/api/generated-api-and-types'
import { MINUTE } from '@/core/constants/constants'
import { StartingPage } from '@/core/constants/startingPage'
import { APP_LOCALE } from '@/locale/constants'

import type { Department } from './companyDepartments'
import type { BaseUserIdentity, LicenseProvider } from './types'
import { createApiMethod, type } from './utils/createApiMethod'
import { stripEmptyQueryParams } from './utils/stripEmptyQueryParams'

export const UserRoleSchema = v.union([
  v.literal('admin'),
  v.literal('company_administrator'),
  v.literal('manager'),
  v.literal('owner'),
  v.literal('public_api_client'),
  v.literal('student'),
  v.literal('content_creator'),
])

export const UserCurrentExtraDataSchema = v.object({
  learngroup: v.optional(
    v.object({
      leaderboard: v.optional(
        v.object({
          since: v.optional(v.union([v.literal('month'), v.literal('total')])),
        }),
      ),
      scoring: v.optional(
        v.object({
          since: v.optional(v.union([v.literal('month'), v.literal('total')])),
        }),
      ),
    }),
  ),
  learning_objective: v.optional(
    v.object({
      congrats_shown: v.optional(v.record(v.string(), v.boolean())),
    }),
  ),
  signup: v.optional(
    v.object({
      privacy_policy_accepted: v.optional(v.boolean()),
    }),
  ),
  pace: v.optional(
    v.object({
      terms_accepted: v.optional(v.boolean()),
    }),
  ),
})

export const UserIdentitySchema = v.object({
  date_of_birth: v.nullable(v.string()),
  email: v.nullable(v.string()),
  first_name: v.nullable(v.string()),
  job_title: v.nullable(v.string()),
  last_name: v.nullable(v.string()),
  no_email_user_id: v.nullable(v.string()),
  phone: v.nullable(v.string()),
  title: v.nullable(v.string()),
})

export const AppLocaleSchema = v.picklist(APP_LOCALE)

export const UserCurrentSchema = v.object({
  avatar_url: v.nullable(v.string()),
  completed_fundamental: v.boolean(),
  current_skill_set: v.boolean(),
  department: v.nullable(v.object({ id: v.string(), name: v.string() })),
  email_pending_update: v.nullable(v.string()),
  extra_data: v.nullable(UserCurrentExtraDataSchema),
  features: v.array(v.string()),
  field_of_activity: v.nullable(v.string()),
  field_of_activity_custom: v.nullable(v.string()),
  global_user_id: v.string(),
  id: v.string(),
  identity: UserIdentitySchema,
  interests: v.array(v.string()),
  lang: AppLocaleSchema,
  licenses: v.object({
    available: v.number(),
    total: v.number(),
  }),
  permission_scopes: v.array(v.string()),
  roles: v.array(UserRoleSchema),
  scorm: v.boolean(),
  settings: v.optional(
    v.object({
      platform_start_page: v.nullable(v.string()),
    }),
  ),
})

export type UserCurrentExtraData = v.InferInput<
  typeof UserCurrentExtraDataSchema
>
export type UserRole = v.InferInput<typeof UserRoleSchema>
export type UserIdentity = v.InferInput<typeof UserIdentitySchema>
export type AppLocale = v.InferInput<typeof AppLocaleSchema>
export type UserCurrent = v.InferInput<typeof UserCurrentSchema>

export type UserStatus = 'active' | 'pending' | 'deactivated'

type UsersBulkRequest = { users_ids: string[] }

type UsersBulkResult = { errors: Record<string, string> }

export type User = {
  avatar_url: string | null
  licenses: {
    available: number
    total: number
  }
  can_be_managed_by_caller: boolean
  can_be_deleted_by_caller: boolean
  department: Department | null
  global_user_id: string
  id: string
  invited_at: string
  is_no_email_user: boolean
  no_email_user_password_reset_at: string | null
  license_provider: LicenseProvider | null
  roles: UserRole[]
  settings?: UserSettings
  status: UserStatus
  user_identity: BaseUserIdentity
}

export type UserSettings = {
  platform_start_page: StartingPage | null
}

export type UserCurrentState = {
  company_courses: {
    num_company_courses_assigned: number
    num_company_courses_completed: number
  }
  learnpaths: {
    num_learnpaths_assigned: number
    num_learnpaths_completed: number
    num_mandatory_learnpaths_assigned: number
    num_mandatory_learnpaths_completed: number
  }
}

export type UsersListQuery = {
  page?: number
  size?: number
  query?: string
  email?: string
  first_name?: string
  last_name?: string
  license_provider_email?: string
  sort_by?: UsersListSort
  order?: UsersListOrder
  roles?: UserRole[]
  from_invited_at?: string
  to_invited_at?: string
  states?: UserStatus[]
  department_id?: string
}

export type UsersListSort =
  | 'status'
  | 'invitation_date'
  | 'department'
  | 'email'
  | 'first_name'
  | 'last_name'
  | 'license_provider_email'
  | 'role'
  | 'available_licenses'
  | 'total_licenses'

export type UsersListOrder = 'asc' | 'desc'

export type UsersListResponse = {
  users: User[]
  total_users: number
}

export interface CurrentUserAvatarPut {
  avatar_data?: string | null
  avatar_id?:
    | 'user_m_1'
    | 'user_m_2'
    | 'user_m_3'
    | 'user_m_4'
    | 'user_m_5'
    | 'user_f_1'
    | 'user_f_2'
    | 'user_f_3'
    | 'user_f_4'
    | 'user_f_5'
    | null
}

export interface CurrentUserPut {
  date_of_birth?: string | null
  field_of_activity?:
    | 'management'
    | 'procurement'
    | 'sales'
    | 'finance'
    | 'it'
    | 'business_intelligence'
    | 'customer_care'
    | 'logistics'
    | 'human_resources'
    | 'consulting'
    | 'coaching'
    | 'product_development'
    | 'graphic_design'
    | 'accounting'
    | 'legal'
    | 'administration'
    | 'marketing'
    | 'public_relations'
    | 'research_development'
    | 'quality_management'
    | 'editorial'
    | 'e_commerce'
    | 'production'
    | 'warehouse'
    | 'other'
    | null
  field_of_activity_custom?: string | null
  first_name?: string
  interests?: (
    | 'design'
    | 'marketing'
    | 'distribution'
    | 'programming'
    | 'hr'
    | 'management'
    | 'product_development'
    | 'new_technologies'
    | 'law'
    | 'it'
    | 'data'
    | 'productivity'
  )[]
  job_title?: string | null
  lang?: AppLocale
  last_name?: string
  phone?: string | null
  title?: 'mr' | 'mrs' | 'mx'
}

export interface CurrentUserExtraDataPut {
  extra_data: object
}

export type CreateUserData = {
  department_id?: string | null
  email: string
  first_name?: string | null
  last_name?: string | null
  lang?: AppLocale
  licenses?: number
  platform_start_page?: StartingPage | null
  roles: UserRole[]
  title?: 'mr' | 'mrs' | 'mx' | null
  send_invite_email?: boolean
}

/**
 * Endpoint: /users
 */
export const users = {
  list: createApiMethod({
    method: 'GET',
    path: '/users',
    query: type<UsersListQuery>(),
    response: type<UsersListResponse>(),
    config: { paramsSerializer: { indexes: null } },
  }),

  details: createApiMethod({
    method: 'GET',
    path: '/users/:userId',
    response: type<User>(),
  }),

  create: createApiMethod({
    method: 'POST',
    path: '/users',
    data: type<CreateUserData>(),
  }),

  update: createApiMethod({
    method: 'PUT',
    path: '/users/:userId',
    data: type<{
      email?: string
      first_name?: string | null
      last_name?: string | null
      title?: 'mr' | 'mrs' | 'mx'
    }>(),
  }),

  current: {
    details: createApiMethod({
      method: 'GET',
      path: '/users/current',
      response: UserCurrentSchema,
    }),
    state: createApiMethod({
      method: 'GET',
      path: '/users/current/state',
      response: type<UserCurrentState>(),
    }),
    update: createApiMethod({
      method: 'PUT',
      path: '/users/current',
      data: type<CurrentUserPut>(),
    }),

    avatar: {
      update: createApiMethod({
        method: 'PUT',
        path: '/users/current/avatar',
        data: type<CurrentUserAvatarPut>(),
      }),
    },
    extraData: {
      update: createApiMethod({
        method: 'PUT',
        path: '/users/current/extra-data',
        data: type<CurrentUserExtraDataPut>(),
      }),
    },
  },

  search: {
    list: createApiMethod({
      method: 'GET',
      path: '/users/search',
      query: type<{
        offset?: number
        size?: number
        query?: string | null
        roles?: UserRole[]
      }>(),
      config: { paramsSerializer: { indexes: null } },
      response: type<UserSearch>(),
    }),
  },

  resetPassword: {
    update: createApiMethod({
      method: 'PUT',
      path: '/users/:userId/reset-password',
      response: type<{ new_password: string }>(),
    }),
  },

  roles: {
    update: createApiMethod({
      method: 'PUT',
      path: '/users/:userId/roles',
      data: type<{ roles: UserRole[] }>(),
    }),
  },

  settings: {
    update: createApiMethod({
      method: 'PATCH',
      path: '/users/:userId/settings',
      data: type<{ platform_start_page: StartingPage | null }>(),
    }),
  },

  departments: {
    update: createApiMethod({
      method: 'PUT',
      path: '/users/:userId/departments',
      data: type<{ department_id: string }>(),
    }),
    delete: createApiMethod({
      method: 'DELETE',
      path: '/users/:userId/departments',
      data: type<{ department_id: string }>(),
    }),
  },

  groups: {
    assign: createApiMethod({
      method: 'POST',
      path: '/users/:userId/groups',
      data: type<UserAssignedGroupsPostRequest>(),
      response: type<UserAssignedGroupsResult>(),
    }),

    unassign: createApiMethod({
      method: 'DELETE',
      path: '/users/:userId/groups',
      data: type<UserAssignedGroupsDeleteRequest>(),
      response: type<UserAssignedGroupsDeleteResult>(),
    }),

    members: {
      exports: {
        create: createApiMethod({
          method: 'POST',
          path: '/users/groups/:userGroupId/members/exports',
          response: type<Blob>(),
          config: { responseType: 'blob' },
        }),
      },
    },
  },

  batchOperations: {
    edit: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/batch-operations/edit',
        data: type<{ file: File }>(),
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      }),
    },
    deactivate: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/batch-operations/deactivate',
        data: type<{ file: File }>(),
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      }),
    },
    reactivate: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/batch-operations/reactivate',
        data: type<{ file: File }>(),
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      }),
    },
    userGroupAssign: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/groups/batch-operations/assign',
        data: type<{ file: File }>(),
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      }),
    },
    departments: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/batch-operations/departments',
        data: type<{ file: File }>(),
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      }),
    },
  },

  exports: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/exports',
      response: type<Blob>(),
      config: { responseType: 'blob' },
    }),
  },

  licenseProvider: {
    update: createApiMethod({
      method: 'PUT',
      path: '/users/:userId/license-provider',
      data: type<{ license_provider_id: string }>(),
    }),
  },

  mail: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/:userId/mail',
    }),
  },

  transferLicenses: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/transfer-licenses',
      data: type<{
        giver_user_id: string
        licenses: number
        receiver_user_id: string
      }>(),
    }),
  },

  deactivate: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/:userId/deactivate',
    }),
  },

  reactivate: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/:userId/reactivate',
    }),
  },
  delete: createApiMethod({
    method: 'DELETE',
    path: '/users/:userId',
  }),

  bulk: {
    deactivate: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/deactivate',
        data: type<UsersBulkRequest>(),
        response: type<UsersBulkResult>(),
      }),
    },
    reactivate: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/reactivate',
        data: type<UsersBulkRequest>(),
        response: type<UsersBulkResult>(),
      }),
    },
    delete: createApiMethod({
      method: 'DELETE',
      path: '/users',
      data: type<UsersBulkRequest>(),
      response: type<UsersBulkResult>(),
    }),
    mail: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/mail',
        data: type<UsersBulkRequest>(),
        response: type<UsersBulkResult>(),
      }),
    },
  },
}

export const usersQueries = {
  all: () => ['users'] as const,
  usersLists: () => ({
    queryKey: [...usersQueries.all(), 'users-list'],
  }),
  usersList: (searchQuery?: UsersListQuery) => {
    const query: UsersListQuery = {
      page: 0,
      size: 10,
      sort_by: 'invitation_date',
      order: 'desc',
      ...stripEmptyQueryParams(searchQuery),
    }

    return queryOptions({
      queryKey: [...usersQueries.usersLists().queryKey, query] as const,
      queryFn: () => users.list({ query }),
      staleTime: MINUTE * 5,
    })
  },
  usersInfiniteLists: () => ({
    queryKey: [...usersQueries.usersLists().queryKey, 'infinite'],
  }),
  usersInfiniteList: (searchQuery?: UsersListQuery) => {
    const query: UsersListQuery = {
      size: 25,
      sort_by: 'invitation_date',
      order: 'desc',
      ...stripEmptyQueryParams(searchQuery),
    }

    return infiniteQueryOptions({
      queryKey: [...usersQueries.usersInfiniteLists().queryKey, query] as const,
      queryFn: ({ pageParam }) =>
        users.list({ query: { ...query, page: pageParam } }),
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        const loaded = sumBy((page) => page.users.length, allPages)
        const hasMore =
          lastPage.users.length > 0 && loaded < lastPage.total_users
        return hasMore ? lastPageParam + 1 : undefined
      },
      initialPageParam: 0,
      staleTime: MINUTE * 5,
      placeholderData: (previousData) => previousData,
    })
  },
  usersDetails: () => ({
    queryKey: [...usersQueries.all(), 'users-detail'],
  }),
  usersDetail: (userId: string) => ({
    queryKey: [...usersQueries.usersDetails().queryKey, userId] as const,
    queryFn: () => users.details({ params: { userId } }),
    staleTime: MINUTE * 30,
  }),
  usersCurrentDetails: () =>
    queryOptions({
      queryKey: [...usersQueries.all(), 'users-current-detail'] as const,
      queryFn: () => users.current.details(),
      staleTime: MINUTE * 5,
    }),
  usersSearchLists: () => ({
    queryKey: [...usersQueries.all(), 'users-search-list'],
  }),
  usersSearchList: (query: {
    offset?: number
    size?: number
    query?: string | null
    roles?: UserRole[]
  }) => ({
    queryKey: [...usersQueries.usersSearchLists().queryKey, query] as const,
    queryFn: () => users.search.list({ query }),
  }),
  usersCurrentState: () =>
    queryOptions({
      queryKey: [...usersQueries.all(), 'users-current-state'] as const,
      queryFn: () => users.current.state(),
    }),
}
